import { createApi } from '@reduxjs/toolkit/query/react';

import fetchBaseQuery from '@app/api/fetchBaseQuery';
import type { SubscribePaymentSetupResponse } from '@app/api/team';

import type {
    AiBillingActivatePackageRequest,
    AiBillingInvoiceDetails,
    AiBillingInvoiceDetailsRequest,
    AiBillingInvoiceDetailsResponse,
    AiBillingListBillersResponse,
    AiBillingListPackagesResponse,
    AiBillingRequestAccessRequest,
    AiBillingStatus,
} from './types';

const aiBillingApi = createApi({
    reducerPath: 'lokaliseInternal_ai_billing',
    baseQuery: fetchBaseQuery(),
    tagTypes: ['AI_ADD_ON_STATUS'],
    endpoints: (builder) => ({
        status: builder.query<AiBillingStatus, { projectId?: string } | void>({
            query: (params) => ({
                method: 'GET',
                url: params ? `/ai-billing/status/${params.projectId}` : `/ai-billing/status`,
            }),
            providesTags: ['AI_ADD_ON_STATUS'],
        }),

        billers: builder.query<AiBillingListBillersResponse, void>({
            query: () => ({
                method: 'GET',
                url: '/ai-billing/billers',
            }),
        }),

        packages: builder.query<AiBillingListPackagesResponse, void>({
            query: () => ({
                method: 'GET',
                url: '/ai-billing/packages',
            }),
        }),

        invoiceDetails: builder.query<AiBillingInvoiceDetails, AiBillingInvoiceDetailsRequest>({
            query: (body) => ({
                method: 'POST',
                url: '/ai-billing/package/upcoming',
                body,
            }),
            transformResponse: async (res: AiBillingInvoiceDetailsResponse) =>
                ({
                    fullPackage: { price: res.fullPackagePrice, wordsCount: res.fullSubscriptionWordsCount },
                    proratedPackage: { price: res.proratedPackagePrice, wordsCount: res.proratedPackageWordsCount },
                    fullSubscription: {
                        price: res.fullSubscriptionPackagesPrice,
                        wordsCount: res.fullSubscriptionWordsCount,
                        currentWordsCount: res.currentSubscriptionWordsCount,
                        usedWordsCount: res.currentSubscriptionUsedWordsCount,
                    },

                    numberOfActiveAddons: res.numberOfActiveAddons,
                    nextBillingCycleDate: res.nextBillingCycleTimestamp * 1000,
                }) satisfies AiBillingInvoiceDetails,
        }),

        requestAccess: builder.mutation<void, AiBillingRequestAccessRequest>({
            query: ({ billerUserId }) => ({
                method: 'POST',
                url: `/ai-billing/request-access`,
                body: { billerUserId },
            }),
        }),

        activate: builder.mutation<SubscribePaymentSetupResponse | { result: string }, AiBillingActivatePackageRequest>(
            {
                query: ({ packageId }) => ({
                    method: 'POST',
                    url: `/ai-billing/activate`,
                    body: { packageId },
                }),
                invalidatesTags: ['AI_ADD_ON_STATUS'],
            },
        ),
    }),
});

export default aiBillingApi;

export const {
    useStatusQuery,
    useBillersQuery,
    usePackagesQuery,
    useInvoiceDetailsQuery,
    useRequestAccessMutation,
    useActivateMutation,
} = aiBillingApi;

export * from './types';
